/** @format */

import React, { useState, useEffect } from "react";
import { doc, getDoc, deleteField } from "firebase/firestore";
import { db } from "../firebase/config";
import {
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  PhotoIcon,
  DocumentArrowDownIcon,
  TagIcon,
  CurrencyDollarIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import {
  getBookCategoryOptions,
  getBookSubcategoryOptions,
  getBookCategoryOptionsWithCustom,
  getBookSubcategoryOptionsWithCustom,
} from "../utils/categoryUtils";
import useFileUpload from "../hooks/useFileUpload";

const BookModal = ({ isOpen, onClose, onSave, document, mode = "add" }) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [pdfName, setPdfName] = useState("");

  const categories = getBookCategoryOptionsWithCustom();
  const categoriesLoading = false;
  const [selectedCategory, setSelectedCategory] = useState(
    document?.category || ""
  );
  const [subcategories, setSubcategories] = useState([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [showCustomSubcategory, setShowCustomSubcategory] = useState(false);
  const [customCategory, setCustomCategory] = useState("");
  const [customSubcategory, setCustomSubcategory] = useState("");
  const { uploadImage, uploadPDF, uploading } = useFileUpload();

  // Update subcategories when category changes
  useEffect(() => {
    if (selectedCategory && selectedCategory !== "custom") {
      const subcategoryOptions =
        getBookSubcategoryOptionsWithCustom(selectedCategory);
      setSubcategories(subcategoryOptions);
      setShowCustomCategory(false);
      setCustomCategory("");
    } else if (selectedCategory === "custom") {
      setSubcategories([]);
      setShowCustomCategory(true);
      setShowCustomSubcategory(false);
      setCustomSubcategory("");
    } else {
      setSubcategories([]);
      setShowCustomCategory(false);
      setShowCustomSubcategory(false);
      setCustomCategory("");
      setCustomSubcategory("");
    }
  }, [selectedCategory]);

  // Handle subcategory selection changes
  const handleSubcategoryChange = (value) => {
    if (value === "custom") {
      setShowCustomSubcategory(true);
      setCustomSubcategory("");
    } else {
      setShowCustomSubcategory(false);
      setCustomSubcategory("");
    }
  };

  useEffect(() => {
    if (isOpen && document && mode !== "add" && typeof document === "object") {
      console.log("Processing document for edit/view:", document);
      Object.keys(document).forEach((key) => {
        if (key !== "id") {
          let value = document[key];
          console.log(`Processing field ${key}:`, value, "type:", typeof value);

          if (value && typeof value === "object" && value._seconds) {
            try {
              // Safely convert Firestore timestamp to ISO string
              console.log(`Converting timestamp for field ${key}:`, value);
              console.log(`Timestamp _seconds:`, value._seconds);
              console.log(`Timestamp _nanoseconds:`, value._nanoseconds);

              const date = new Date(value._seconds * 1000);
              console.log(`Converted date for ${key}:`, date);
              console.log(`Date getTime():`, date.getTime());
              console.log(`Date isValid:`, !isNaN(date.getTime()));

              if (!isNaN(date.getTime())) {
                value = date.toISOString().slice(0, 16);
                console.log(`Final ISO string for ${key}:`, value);
              } else {
                // Invalid timestamp, use current date
                console.warn(
                  `Invalid timestamp for ${key}, using current date`
                );
                value = new Date().toISOString().slice(0, 16);
              }
            } catch (error) {
              console.warn("Error converting Firestore timestamp:", error);
              // Fallback to current date
              value = new Date().toISOString().slice(0, 16);
            }
          }
          setValue(key, value);
        }
      });

      // Prefill author field from creator user record (createdBy) when available
      (async () => {
        try {
          const creatorId =
            document.createdBy || document.createdByUid || document.userId;
          if (creatorId) {
            const userRef = doc(db, "users", String(creatorId));
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              const displayName =
                userData.name || userData.displayName || userData.email || "";
              if (displayName) {
                setValue("author", displayName);
              }
            }
          }
        } catch (e) {
          console.warn("Error fetching creator name for book:", e);
        }
      })();

      if (typeof document.image_url === "string" && document.image_url) {
        setImagePreview(document.image_url);
        setValue("image_url", document.image_url);
      } else {
        setImagePreview("");
        setValue("image_url", "");
      }
      if (typeof document.pdf_url === "string" && document.pdf_url) {
        setPdfName(document.pdf_url.split("/").pop() || "PDF uploaded");
        setValue("pdf_url", document.pdf_url);
      } else {
        setPdfName("");
        setValue("pdf_url", "");
      }
    } else if (isOpen && mode === "add") {
      if (reset && typeof reset === "function") {
        // reset();
      }
      setSelectedImage(null);
      setSelectedPDF(null);
      setImagePreview("");
      setPdfName("");
      setValue("image_url", "");
      setValue("pdf_url", "");
    }
  }, [isOpen, document, mode, setValue, reset]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        alert(
          "Image file is too large. Please select an image smaller than 10MB."
        );
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePDFChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB limit)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > maxSize) {
        alert("PDF file is too large. Please select a PDF smaller than 50MB.");
        return;
      }

      setSelectedPDF(file);
      setPdfName(file.name);
    }
  };

  const onSubmit = async (data) => {
    console.log("Form submitted with data:", data);
    console.log("Selected image:", selectedImage);
    console.log("Selected PDF:", selectedPDF);

    // Validate custom fields
    if (data.category === "custom" && !customCategory.trim()) {
      alert("Please enter a custom category name");
      return;
    }

    if (data.subcategory === "custom" && !customSubcategory.trim()) {
      alert("Please enter a custom subcategory name");
      return;
    }

    setLoading(true);
    try {
      let imageUrl = data.image_url;
      let pdfUrl = data.pdf_url;

      // Only upload new files if they are selected
      if (selectedImage) {
        console.log("Uploading new image:", selectedImage.name);
        imageUrl = await uploadImage(selectedImage, selectedImage.name);
        console.log("New image uploaded, URL:", imageUrl);
      } else {
        console.log("No new image selected, keeping existing image URL:", imageUrl);
      }
      
      if (selectedPDF) {
        console.log("Uploading new PDF:", selectedPDF.name);
        pdfUrl = await uploadPDF(selectedPDF, selectedPDF.name);
        console.log("New PDF uploaded, URL:", pdfUrl);
      } else {
        console.log("No new PDF selected, keeping existing PDF URL:", pdfUrl);
      }

      // Remove file objects from data before saving to Firestore
      const { image_file, pdf_file, ...cleanData } = data;

      console.log("Clean data after removing file objects:", cleanData);
      console.log("Clean data createdAt:", cleanData.createdAt);
      console.log("Clean data createdAt type:", typeof cleanData.createdAt);

      // Handle date fields safely
      let createdAt;
      if (cleanData.createdAt) {
        try {
          // If it's already a Date object, use it
          if (cleanData.createdAt instanceof Date) {
            createdAt = cleanData.createdAt;
            console.log("Using existing Date object for createdAt:", createdAt);
          } else if (typeof cleanData.createdAt === "string") {
            // If it's a string, try to parse it
            console.log("Parsing createdAt string:", cleanData.createdAt);
            const parsedDate = new Date(cleanData.createdAt);
            console.log("Parsed date result:", parsedDate);
            if (isNaN(parsedDate.getTime())) {
              // Invalid date string, use current date
              console.warn("Invalid date string, using current date");
              createdAt = new Date();
            } else {
              createdAt = parsedDate;
              console.log("Successfully parsed date:", createdAt);
            }
          } else if (
            cleanData.createdAt &&
            typeof cleanData.createdAt === "object" &&
            cleanData.createdAt._seconds
          ) {
            // If it's a Firestore timestamp, convert it
            console.log("Converting Firestore timestamp:", cleanData.createdAt);
            createdAt = new Date(cleanData.createdAt._seconds * 1000);
            console.log("Converted timestamp to date:", createdAt);
          } else {
            // Fallback to current date
            console.warn("Unknown createdAt format, using current date");
            createdAt = new Date();
          }
        } catch (error) {
          console.warn("Error parsing createdAt date:", error);
          createdAt = new Date();
        }
      } else {
        console.log("No createdAt provided, using current date");
        createdAt = new Date();
      }

      // Final safety check for createdAt
      if (!(createdAt instanceof Date) || isNaN(createdAt.getTime())) {
        console.error("Invalid createdAt date detected, using current date");
        createdAt = new Date();
      }

      // Check for any other date fields that might need validation
      const processedData = { ...cleanData };
      Object.keys(processedData).forEach((key) => {
        if (key !== "createdAt" && key !== "updatedAt" && key !== "id") {
          const value = processedData[key];
          if (value && typeof value === "object" && value._seconds) {
            try {
              const date = new Date(value._seconds * 1000);
              if (!isNaN(date.getTime())) {
                processedData[key] = date;
              } else {
                console.warn(`Invalid date field ${key}, removing it`);
                delete processedData[key];
              }
            } catch (error) {
              console.warn(`Error processing date field ${key}:`, error);
              delete processedData[key];
            }
          }
        }
      });

      // Handle custom category and subcategory
      let finalCategory = processedData.category;
      let finalSubcategory = processedData.subcategory;

      if (processedData.category === "custom" && customCategory.trim()) {
        finalCategory = customCategory.trim();
      }

      if (processedData.subcategory === "custom" && customSubcategory.trim()) {
        finalSubcategory = customSubcategory.trim();
      }

      const bookData = {
        ...processedData,
        category: finalCategory,
        subcategory: finalSubcategory,
        amount: Number(processedData.amount) || 0,
        createdAt: createdAt,
        updatedAt: new Date(),
      };

      // Resolve image_url field based on user actions
      if (selectedImage && imageUrl) {
        // New image uploaded
        bookData.image_url = imageUrl;
      } else if (!selectedImage) {
        if (processedData.image_url === "") {
          // User cleared the image
          bookData.image_url = mode === "edit" ? deleteField() : "";
        } else if (document?.image_url) {
          // Preserve existing if not cleared
          bookData.image_url = document.image_url;
        } else if (typeof processedData.image_url === "string") {
          bookData.image_url = processedData.image_url;
        }
      }

      // Resolve pdf_url field based on user actions
      if (selectedPDF && pdfUrl) {
        // New PDF uploaded
        bookData.pdf_url = pdfUrl;
      } else if (!selectedPDF) {
        if (processedData.pdf_url === "") {
          // User cleared the PDF
          bookData.pdf_url = mode === "edit" ? deleteField() : "";
        } else if (document?.pdf_url) {
          // Preserve existing if not cleared
          bookData.pdf_url = document.pdf_url;
        } else if (typeof processedData.pdf_url === "string") {
          bookData.pdf_url = processedData.pdf_url;
        }
      }

      console.log("=== FINAL BOOK DATA ===");
      console.log("Saving book data:", bookData);
      console.log("CreatedAt value:", bookData.createdAt);
      console.log("CreatedAt type:", typeof bookData.createdAt);
      console.log(
        "CreatedAt instanceof Date:",
        bookData.createdAt instanceof Date
      );
      if (bookData.createdAt instanceof Date) {
        console.log("CreatedAt toISOString:", bookData.createdAt.toISOString());
        console.log("CreatedAt getTime:", bookData.createdAt.getTime());
        console.log("CreatedAt isValid:", !isNaN(bookData.createdAt.getTime()));
      }
      console.log("UpdatedAt value:", bookData.updatedAt);
      console.log("UpdatedAt type:", typeof bookData.updatedAt);
      console.log(
        "UpdatedAt instanceof Date:",
        bookData.updatedAt instanceof Date
      );
      if (bookData.updatedAt instanceof Date) {
        console.log("UpdatedAt toISOString:", bookData.updatedAt.toISOString());
      }
      console.log("=== END BOOK DATA ===");

      await onSave(bookData);
      onClose();
      if (reset && typeof reset === "function") {
        // reset();
      }
    } catch (error) {
      console.error("Error saving book:", error);
      alert(`Error saving book: ${error.message || "Unknown error occurred"}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isReadOnly = mode === "view";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <BookOpenIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {mode === "add"
                  ? "Add New Book"
                  : mode === "edit"
                  ? "Edit Book"
                  : "View Book"}
              </h2>
              <p className="text-sm text-gray-500">
                {mode === "add"
                  ? "Create a new book entry"
                  : mode === "edit"
                  ? "Modify book information"
                  : "View book details"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Basic Information Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-2" />
              Basic Information
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Book Title *
                </label>
                <input
                  type="text"
                  {...register("title", { required: "Title is required" })}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                  placeholder="Enter book title..."
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.title.message}
                  </p>
                )}
              </div>

              {/* Author */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  {...register("author", { required: "Author is required" })}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                  placeholder="Enter author name..."
                />
                {errors.author && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.author.message}
                  </p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  {...register("category", {
                    required: "Category is required",
                  })}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={isReadOnly || categoriesLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>

                {/* Custom Category Input */}
                {showCustomCategory && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category name..."
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                )}
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.category.message}
                  </p>
                )}
                {showCustomCategory && !customCategory.trim() && (
                  <p className="mt-1 text-sm text-red-600">
                    Please enter a custom category name
                  </p>
                )}
              </div>

              {/* Subcategory */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subcategory
                </label>
                <select
                  {...register("subcategory")}
                  onChange={(e) => handleSubcategoryChange(e.target.value)}
                  disabled={
                    isReadOnly ||
                    !selectedCategory ||
                    subcategories.length === 0
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                >
                  <option value="">Select a subcategory (optional)</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>

                {/* Custom Subcategory Input */}
                {showCustomSubcategory && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={customSubcategory}
                      onChange={(e) => setCustomSubcategory(e.target.value)}
                      placeholder="Enter custom subcategory name..."
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    {!customSubcategory.trim() && (
                      <p className="mt-1 text-sm text-red-600">
                        Please enter a custom subcategory name
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (GHS)
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    {...register("amount")}
                    disabled={isReadOnly}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                {...register("description")}
                disabled={isReadOnly}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                placeholder="Enter book description..."
              />
            </div>
          </div>

          {/* FILE UPLOAD SECTION - THIS IS WHAT YOU'RE LOOKING FOR */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <DocumentArrowDownIcon className="h-5 w-5 text-green-600 mr-2" />
              Book Files
            </h4>

            {/* Upload Instructions */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📁 Upload Instructions:</strong> You can upload a book
                cover image and a PDF file for the book content. Images should
                be in PNG, JPG, or GIF format (max 10MB). PDFs should be under
                50MB.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📷 Book Cover Image
                </label>
                <div className="space-y-4">
                  {imagePreview && (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Book cover preview"
                        className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                      />
                      {!isReadOnly && (
                                                  <button
                            type="button"
                            onClick={() => {
                              setImagePreview("");
                              setSelectedImage(null);
                              setValue("image_url", "");
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                          >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {!isReadOnly && (
                    <div className="flex items-center justify-center w-full">
                      <label
                        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          selectedImage
                            ? "border-green-300 bg-green-50 hover:bg-green-100"
                            : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <PhotoIcon
                            className={`w-8 h-8 mb-2 ${
                              selectedImage ? "text-green-500" : "text-gray-400"
                            }`}
                          />
                          <p
                            className={`mb-2 text-sm ${
                              selectedImage ? "text-green-600" : "text-gray-500"
                            }`}
                          >
                            <span className="font-semibold">
                              {selectedImage
                                ? "✅ Image Selected!"
                                : "Click to upload"}
                            </span>
                            {!selectedImage && " or drag and drop"}
                          </p>
                          <p
                            className={`text-xs ${
                              selectedImage ? "text-green-500" : "text-gray-500"
                            }`}
                          >
                            {selectedImage
                              ? selectedImage.name
                              : "PNG, JPG, GIF up to 10MB"}
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}
                  
                  {/* Hidden input to preserve existing image URL */}
                  <input
                    type="hidden"
                    {...register("image_url")}
                  />
                </div>
              </div>

              {/* PDF Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📄 Book PDF
                </label>
                <div className="space-y-4">
                  {pdfName && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <DocumentArrowDownIcon className="h-8 w-8 text-blue-600 mr-3" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-blue-900 truncate">
                            {pdfName}
                          </p>
                          <p className="text-xs text-blue-600">PDF Document</p>
                        </div>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => {
                              setPdfName("");
                              setSelectedPDF(null);
                              setValue("pdf_url", "");
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isReadOnly && (
                    <div className="flex items-center justify-center w-full">
                      <label
                        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          selectedPDF
                            ? "border-blue-300 bg-blue-50 hover:bg-blue-100"
                            : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <DocumentArrowDownIcon
                            className={`w-8 h-8 mb-2 ${
                              selectedPDF ? "text-blue-500" : "text-gray-400"
                            }`}
                          />
                          <p
                            className={`mb-2 text-sm ${
                              selectedPDF ? "text-blue-600" : "text-gray-500"
                            }`}
                          >
                            <span className="font-semibold">
                              {selectedPDF
                                ? "✅ PDF Selected!"
                                : "Click to upload"}
                            </span>
                            {!selectedPDF && " or drag and drop"}
                          </p>
                          <p
                            className={`text-xs ${
                              selectedPDF ? "text-blue-500" : "text-gray-500"
                            }`}
                          >
                            {selectedPDF
                              ? selectedPDF.name
                              : "PDF files up to 50MB"}
                          </p>
                        </div>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handlePDFChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}
                  
                  {/* Hidden input to preserve existing PDF URL */}
                  <input
                    type="hidden"
                    {...register("pdf_url")}
                  />
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-sm text-blue-700">
                      Uploading files...
                    </span>
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: "100%" }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      Please wait while your files are being uploaded...
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Information Section */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <TagIcon className="h-5 w-5 text-purple-600 mr-2" />
              Additional Information
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ISBN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ISBN
                </label>
                <input
                  type="text"
                  {...register("isbn")}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors disabled:bg-gray-100"
                  placeholder="Enter ISBN number..."
                />
              </div>

              {/* Publication Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publication Date
                </label>
                <input
                  type="date"
                  {...register("publicationDate")}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors disabled:bg-gray-100"
                />
              </div>

              {/* Pages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Pages
                </label>
                <input
                  type="number"
                  {...register("pages")}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors disabled:bg-gray-100"
                  placeholder="Enter number of pages..."
                />
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <input
                  type="text"
                  {...register("language")}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors disabled:bg-gray-100"
                  placeholder="e.g., English, French..."
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            {!isReadOnly && (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>{mode === "add" ? "Create Book" : "Update Book"}</>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookModal;
